'use strict';

import log from 'fancy-log';
import pluginError from 'plugin-error';
import through2 from 'through2';
import Vinyl from 'vinyl';
import * as util from '../utilities';

const PLUGIN_NAME = 'gulp-ps-cim';

interface MapData {
    name: string;
    description: string;
    scope: string;
    roles: string[];
    type: string;
    namespace: string;
    className: string;
    methodName: string;
    query: string;
    keyProperties: [
        {
            name: string;
            type: string;
        }];
    property: [
        {
            name: string;
            type: string;
            value: any;
        }];
    arguments: [
        {
            name: string;
            type: string;
            value: any;
        }];
}

function gulpPowerShellCim(_) {
    let collection: MapData[] = null;

    return through2.obj(
        /**
         * Transform
         */
        function (file, encoding, callback) {
            collection = JSON.parse(file.contents.toString()).collection;
            return callback();
        },
        /**
         * Flush
         */
        function (callback) {
            try {
                for (const mapData of collection) {
                    const contents = cimScript(mapData).join('\r\n');
                    const scopePath = mapData.scope ? mapData.scope + '/' : '';
                    const scriptFile = new Vinyl({
                        cwd: './',
                        path: scopePath + mapData.name + '.ps1',
                        contents: Buffer.from(contents, 'utf8')
                    });
                    this.push(scriptFile);
                }
            } catch (e) {
                const error = (!e.plugin || (e.plugin !== PLUGIN_NAME)) ?
                    util.extendError(new pluginError({ plugin: PLUGIN_NAME, message: e.message }), e) : e;
                log.error(error);
            }

            callback();
        });
}

function cimScript(mapData: MapData): string[] {
    let line: string;
    const bodySection: string[] = [];
    const commentSection: string[] = [];
    let paramSection: string[] = [];
    let parameters;
    commentSection.push(
        '<#',
        '',
        '.SYNOPSIS',
        mapData.description,
        '',
        '.DESCRIPTION',
        mapData.description,
        '',
        '.ROLE');
    commentSection.push(...mapData.roles),
    commentSection.push(
        '',
        '#>',
        '',
        '##SkipCheck=true##');

    switch (mapData.type) {
        case 'getInstanceMultiple':
        case 'getBatchInstanceMultiple':
            // namespace the cim namespace.
            // className the class name.
            line = `Get-CimInstance -Namespace ${mapData.namespace} -ClassName ${mapData.className}`;
            bodySection.push(line);
            break;

        case 'getInstanceSingle':
        case 'getBatchInstanceSingle':
            // namespace the cim namespace.
            // lassName the class name.
            // keyProperties the key properties object
            parameters = keyArgumentsParameters(mapData);
            paramSection = parameters.paramSection;
            line = `$keyInstance = New-CimInstance -Namespace ${mapData.namespace} -ClassName ${mapData.className}`;
            line += ` -Key @(${parameters.keyLine}) -Property @{${parameters.propertyLine}} -ClientOnly`;
            bodySection.push(line);
            line = 'Get-CimInstance $keyInstance';
            bodySection.push(line);
            break;

        case 'invokeMethodInstance':
        case 'invokeBatchMethodInstance':
            // namespace the cim namespace.
            // className the class name.
            // methodName the method name.
            // keyProperties the key properties object.
            // data the method input data.
            parameters = keyArgumentsParameters(mapData);
            paramSection = parameters.paramSection;
            line = `$keyInstance = New-CimInstance -Namespace ${mapData.namespace} -ClassName ${mapData.className}`;
            line += ` -Key @(${parameters.keyLine}) -Property @{${parameters.propertyLine}} -ClientOnly`;
            bodySection.push(line);
            line = `Invoke-CimMethod $keyInstance -MethodName ${mapData.methodName}`;
            if (parameters.argumentsLine) {
                line += ` -Arguments @{${parameters.argumentsLine}}`;
            }

            bodySection.push(line);
            break;

        case 'invokeMethodStatic':
        case 'invokeBatchMethodStatic':
            // namespace the cim namespace.
            // className the class name.
            // methodName the method name.
            // data the method input data.
            parameters = keyArgumentsParameters(mapData);
            paramSection = parameters.paramSection;
            line = `Invoke-CimMethod -Namespace ${mapData.namespace} -ClassName ${mapData.className}`;
            line += ` -MethodName ${mapData.methodName}`;
            if (parameters.argumentsLine) {
                line += ` -Arguments @{${parameters.argumentsLine}}`;
            }

            bodySection.push(line);
            break;

        case 'setInstance':
        case 'setBatchInstance':
            // namespace the cim namespace.
            // lassName the class name.
            // keyProperties the key properties object.
            // data the method input data.
            parameters = keyArgumentsParameters(mapData);
            paramSection = parameters.paramSection;
            line = `New-CimInstance -Namespace ${mapData.namespace} -ClassName ${mapData.className}`;
            line += ` -Key @(${parameters.keyLine}) -Property @{${parameters.propertyLine}${parameters.argumentsLine}}`;
            bodySection.push(line);
            break;

        case 'modifyInstance':
        case 'modifyBatchInstance':
            // namespace the cim namespace.
            // className the class name.
            // keyProperties the key properties object.
            // data the method input data.
            parameters = keyArgumentsParameters(mapData);
            paramSection = parameters.paramSection;
            line = `$instance = New-CimInstance -Namespace ${mapData.namespace} -ClassName ${mapData.className}`;
            line += ` -Key @(${parameters.keyLine}) -Property @{${parameters.propertyLine}} -ClientOnly`;
            bodySection.push(line);
            line = '$instance = Get-CimInstance $instance';
            bodySection.push(line);
            bodySection.push(...parameters.updateSection);
            line = 'Set-CimInstance $instance';
            bodySection.push(line);
            break;

        case 'deleteInstance':
        case 'deleteBatchInstance':
            // namespace the cim namespace.
            // className the class name.
            // keyProperties the key properties object.
            parameters = keyArgumentsParameters(mapData);
            paramSection = parameters.paramSection;
            line = `$instance = New-CimInstance -Namespace ${mapData.namespace} -ClassName ${mapData.className}`;
            line += ` -Key @(${parameters.keyLine}) -Property @{${parameters.propertyLine}} -ClientOnly`;
            bodySection.push(line);
            line = 'Remove-CimInstance $instance';
            bodySection.push(line);
            break;

        case 'getInstanceQuery':
        case 'getBatchInstanceQuery':
            // namespace the cim namespace.
            // query the WQL string.
            parameters = keyArgumentsParameters(mapData);
            paramSection = parameters.paramSection;
            line = `Get-CimInstance -Namespace ${mapData.namespace} -Query "${mapData.query}"`;
            bodySection.push(line);
            break;
    }

    const content: string[] = [];
    content.push(...commentSection);
    content.push('');
    content.push(...paramSection);
    content.push('');
    content.push('import-module CimCmdlets');
    content.push('');
    content.push(...bodySection);
    content.push('');
    return content;
}

function keyArgumentsParameters(mapData: MapData): {
    keyLine: string,
    propertyLine: string,
    argumentsLine: string,
    updateSection: string[],
    paramSection: string[]
} {
    const paramSection: string[] = [];
    paramSection.push('Param(');
    let keyLine: string = null;
    let keySeparator = '';
    let propLine: string = null;
    let dataLine: string = null;
    if (mapData.keyProperties && Array.isArray(mapData.keyProperties)) {
        keyLine = '';
        propLine = '';
        for (const key of mapData.keyProperties) {
            paramSection.push(`[${key.type}]$${key.name}`);
            keyLine += `${keySeparator}'${key.name}'`;
            propLine += `${key.name}=$${key.name};`;
            keySeparator = ',';
        }
    }

    const updateSection: string[] = [];
    updateSection.push('');
    if (mapData.arguments) {
        dataLine = '';
        for (const data of mapData.arguments) {
            paramSection.push(`[${data.type}]$${data.name}`);
            dataLine += `${data.name}=$${data.name};`;
            updateSection.push(`$instance.${data.name}=$${data.name}`);
        }
    }

    if (paramSection.length > 2) {
        for (let i = 1; i < paramSection.length - 1; i++) {
            paramSection[i] += ',';
        }
    }

    paramSection.push(')');
    return {
        keyLine: keyLine,
        propertyLine: propLine,
        argumentsLine: dataLine,
        updateSection: updateSection,
        paramSection: paramSection
    };
}

module.exports = gulpPowerShellCim;
